import { useEffect, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { config } from '../libs/config';

const RBAC_BASE_URL = config.rbacBaseUrl;
const RBAC_APP_ID = config.rbacAppId;
const RBAC_TENANT_NAME = config.rbacTenantName;

// Redirect flow, not loginPopup() — returns to the app root itself, so no dedicated
// bridge page is needed the way the Vite-based samples in this RBAC ecosystem use
// (auth-popup.html). esb-admin runs plain CRA with no active webpack customization
// to add a second HTML entry point (react-app-rewired is a dependency but the
// package.json scripts call react-scripts directly, so config-overrides.js is dead
// code today) — see login.js for the loginRedirect()/handleRedirectPromise() flow
// this redirectUri is paired with.
const redirectUri = window.location.origin;

let instance = null;
let ssoEnabled = false;
let initPromise = null;

/**
 * Fetches this application's Entra SSO config for THIS tenant from RBAC (GET
 * /apps/{appId}/sso-config?tenantName=...) and builds the MSAL instance from it — SSO is
 * configured per (app, RBAC tenant) on the Applications page's Entra ID SSO tab, not per-app,
 * so the tenant name must be supplied or the endpoint always reports disabled. If SSO isn't
 * configured for this app/tenant (or the fetch fails), falls back to an inert instance with a placeholder
 * clientId instead of null — login.js and signout.js both call useMsal(), which throws
 * without a real MsalProvider ancestor, so there always needs to be *something* valid to
 * wrap with. The inert instance never actually contacts Azure unless a login is attempted;
 * isSsoEnabled() is what actually gates the "Sign in with Microsoft" button's visibility.
 * Idempotent: safe to call from multiple components, only fetches once.
 */
export function initMsal() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    // Local mode has no RBAC to ask, so Entra is configured straight from the deployment's
    // own REACT_APP_SSO_* values - the pre-RBAC arrangement, kept so a client that has not
    // adopted RBAC still gets single sign-on from the same image.
    if (config.authMode !== 'rbac') {
      if (config.ssoEnabled && config.ssoClientId && config.ssoTenantId) {
        ssoEnabled = true;
        instance = new PublicClientApplication({
          auth: {
            clientId: config.ssoClientId,
            authority: `https://login.microsoftonline.com/${config.ssoTenantId}`,
            redirectUri,
            postLogoutRedirectUri: redirectUri,
          },
          cache: { cacheLocation: 'sessionStorage' },
        });
        return instance;
      }

      ssoEnabled = false;
      instance = new PublicClientApplication({ auth: { clientId: '', redirectUri } });
      return instance;
    }

    try {
      const resp = await fetch(
        `${RBAC_BASE_URL}/apps/${encodeURIComponent(RBAC_APP_ID)}/sso-config?tenantName=${encodeURIComponent(RBAC_TENANT_NAME)}`,
      );
      if (resp.ok) {
        const config = await resp.json();
        if (config.ssoEnabled && config.clientId && config.entraTenantId) {
          ssoEnabled = true;
          instance = new PublicClientApplication({
            auth: {
              clientId: config.clientId,
              authority: `https://login.microsoftonline.com/${config.entraTenantId}`,
              redirectUri,
              postLogoutRedirectUri: redirectUri,
            },
            cache: {
              cacheLocation: 'sessionStorage',
            },
          });
          return instance;
        }
      }
    } catch {
      // fall through to the inert fallback below
    }

    ssoEnabled = false;
    instance = new PublicClientApplication({
      auth: { clientId: '', redirectUri },
    });
    return instance;
  })();
  return initPromise;
}

/** True once initMsal() has resolved to a real, RBAC-configured Entra app. */
export function isSsoEnabled() {
  return ssoEnabled;
}

/**
 * React hook wrapping initMsal() — triggers the fetch on first mount (idempotent across
 * multiple components) and re-renders once resolved. Returns null only until resolved;
 * after that it's always a real (or inert-fallback) PublicClientApplication, never null.
 */
export function useMsalInstance() {
  const [resolved, setResolved] = useState(instance);

  useEffect(() => {
    let cancelled = false;
    initMsal().then((inst) => {
      if (!cancelled) setResolved(inst);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return resolved;
}
